import { useState } from "react";
import { Play, Pause, Archive, Pencil, RotateCcw, Users, Plus } from "lucide-react";
import { STATUS_STYLES, SkeletonRows, EmptyState } from "./ClientDialerUi";

const BUCKETS = [["active", "Active"], ["paused", "Paused"], ["archived", "Archived"]];
const iconBtn = { fontSize: 11, padding: "5px 9px", display: "inline-flex", alignItems: "center", gap: 4 };

function StatusTabs({ value, onChange, counts }) {
  return (
    <div data-testid="cd-status-filter" style={{ display: "flex", gap: 6, marginBottom: 4, fontSize: 12, flexWrap: "wrap", alignItems: "center" }}>
      {BUCKETS.map(([id, label]) => {
        const on = value === id;
        return (
          <button key={id} data-testid={`cd-status-${id}`} onClick={() => onChange(id)}
            style={{ padding: "5px 12px", borderRadius: 6, fontFamily: "inherit", fontSize: 12, cursor: "pointer", fontWeight: on ? 600 : 500, background: on ? "#0a2540" : "#fff", color: on ? "#fff" : "#374151", border: `0.5px solid ${on ? "#0a2540" : "#e5e7eb"}`, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span>{label}</span>
            <span style={{ padding: "1px 7px", borderRadius: 999, fontSize: 10, fontWeight: 600, background: on ? "rgba(255,255,255,0.18)" : "#f1f5f9", color: on ? "#fff" : "#64748b" }}>{counts[id]}</span>
          </button>
        );
      })}
    </div>
  );
}

function CampaignCard({ c, isManager, onStart, onEdit, onStatus }) {
  const st = STATUS_STYLES[c.status] || STATUS_STYLES.active;
  const called = Math.min(c.called_count || 0, c.client_count || 0);
  const pct = c.client_count ? (called / c.client_count) * 100 : 0;
  return (
    <div data-testid={`cd-campaign-${c.id}`} style={{ background: "#fff", borderRadius: 10, border: "0.5px solid #eee", borderLeft: `4px solid ${st.border}`, padding: "14px 18px", display: "flex", alignItems: "center", gap: 14 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
          <span style={{ fontSize: 14, fontWeight: 600 }}>{c.name}</span>
          <span data-testid={`cd-campaign-status-${c.id}`} style={{ padding: "2px 8px", borderRadius: 10, fontSize: 10, background: st.bg, color: st.color, fontWeight: 500, textTransform: "capitalize" }}>{c.status}</span>
          <span style={{ padding: "2px 8px", borderRadius: 10, fontSize: 10, background: "#EEEDFE", color: "#3C3489" }}>{c.client_count} {c.client_count === 1 ? "client" : "clients"}</span>
          {(c.assigned_rep_names || []).length > 0 && (
            <span title={c.assigned_rep_names.join(", ")} style={{ padding: "2px 8px", borderRadius: 10, fontSize: 10, background: "#E6F1FB", color: "#185FA5", fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 4 }}>
              <Users style={{ width: 9, height: 9 }} /> {c.assigned_rep_names.length}
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, color: "#888", display: "flex", alignItems: "center", gap: 10 }}>
          <span data-testid={`cd-campaign-progress-${c.id}`}>{called} / {c.client_count} clients called</span>
          <div style={{ width: 120, height: 4, background: "#f1f5f9", borderRadius: 2 }}><div style={{ width: `${pct}%`, height: "100%", background: st.border, borderRadius: 2 }} /></div>
          <span data-testid={`cd-campaign-left-${c.id}`} style={{ color: "#0F766E", fontWeight: 600 }}>{Math.max(0, (c.client_count || 0) - called)} left to call</span>
          {c.created_at && <span data-testid={`cd-campaign-created-${c.id}`}>Created {new Date(c.created_at).toLocaleDateString()}{c.created_by_name ? ` by ${c.created_by_name}` : ""}</span>}
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(c.assigned_rep_names || []).join(", ")}</span>
        </div>
      </div>
      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
        {c.status === "active" && <button data-testid={`cd-start-${c.id}`} className="btn btn-primary" style={iconBtn} onClick={() => onStart(c)}><Play style={{ width: 11, height: 11 }} /> Start calling</button>}
        {isManager && c.status !== "archived" && <button data-testid={`cd-edit-${c.id}`} className="btn" style={iconBtn} onClick={() => onEdit(c)}><Pencil style={{ width: 11, height: 11 }} /> Edit</button>}
        {isManager && c.status === "active" && <button data-testid={`cd-pause-${c.id}`} className="btn" style={iconBtn} onClick={() => onStatus(c, "paused")}><Pause style={{ width: 11, height: 11 }} /> Pause</button>}
        {isManager && c.status !== "active" && <button data-testid={`cd-resume-${c.id}`} className="btn" style={iconBtn} onClick={() => onStatus(c, "active")}><RotateCcw style={{ width: 11, height: 11 }} /> Resume</button>}
        {isManager && c.status !== "archived" && <button data-testid={`cd-archive-${c.id}`} className="btn" style={{ ...iconBtn, color: "#A32D2D" }} onClick={() => onStatus(c, "archived")}><Archive style={{ width: 11, height: 11 }} /> Archive</button>}
      </div>
    </div>
  );
}

/** Leads-style Campaigns tab for client campaigns. */
export default function ClientCampaignsList({ campaigns, loading, isManager, onNew, onStart, onEdit, onStatus }) {
  const [bucket, setBucket] = useState("active");
  const counts = Object.fromEntries(BUCKETS.map(([id]) => [id, campaigns.filter((c) => c.status === id).length]));
  const visible = campaigns.filter((c) => c.status === bucket);
  return (
    <div>
      {isManager && (
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: 10 }}>
          <button data-testid="cd-new-campaign" onClick={onNew} className="btn btn-primary" style={{ fontSize: 12, whiteSpace: "nowrap" }}><Plus style={{ width: 12, height: 12 }} /> New Campaign</button>
        </div>
      )}
      {loading && <SkeletonRows />}
      {!loading && campaigns.length === 0 && (
        <EmptyState testid="cd-campaigns-empty">No campaigns yet. Click <strong>New Campaign</strong> to create your first one.</EmptyState>
      )}
      {!loading && campaigns.length > 0 && (
        <div data-testid="cd-campaigns-list" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <StatusTabs value={bucket} onChange={setBucket} counts={counts} />
          {visible.length === 0 && <EmptyState testid="cd-bucket-empty">No {bucket} campaigns.</EmptyState>}
          {visible.map((c) => <CampaignCard key={c.id} c={c} isManager={isManager} onStart={onStart} onEdit={onEdit} onStatus={onStatus} />)}
        </div>
      )}
    </div>
  );
}
